import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { api } from '../services/api';
import { X, Clock } from 'lucide-react';

interface TimerSummaryModalProps {
  ticketId: string;
  ticketTitle: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  onClose: () => void;
  onSuccess: () => void;
  readonlyTicket?: boolean;
}

export default function TimerSummaryModal({
  ticketId,
  ticketTitle,
  startTime,
  endTime,
  durationMinutes,
  onClose,
  onSuccess,
  readonlyTicket = true,
}: TimerSummaryModalProps) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState('');
  const [billable, setBillable] = useState(true);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');

  useEffect(() => {
    const toLocal = (iso: string) => {
      const d = new Date(iso);
      const offset = d.getTimezoneOffset();
      const local = new Date(d.getTime() - offset * 60000);
      return local.toISOString().slice(0, 16);
    };
    setEditStartTime(toLocal(startTime));
    setEditEndTime(toLocal(endTime));
  }, [startTime, endTime]);

  const computedDuration = (() => {
    if (!editStartTime || !editEndTime) return durationMinutes;
    const diff = new Date(editEndTime).getTime() - new Date(editStartTime).getTime();
    return Math.max(1, Math.round(diff / 60000));
  })();

  const mutation = useMutation({
    mutationFn: () => api.createWorkLog({
      ticket_id: ticketId,
      start_time: new Date(editStartTime).toISOString(),
      end_time: new Date(editEndTime).toISOString(),
      summary,
      billable,
      source: 'timer',
    } as any),
    onSuccess,
  });

  const isValid = summary.trim().length >= 20;

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="modal-backdrop z-[60]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-lg w-full p-6" dir="rtl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-surface-900 flex items-center gap-2">
            <Clock size={20} className="text-primary-600" />
            {t('timer.summary')}
          </h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('timer.ticketLabel')}</label>
            {readonlyTicket ? (
              <p className="text-sm font-medium text-surface-800 py-2 px-3 bg-surface-50 rounded-lg">{ticketTitle}</p>
            ) : (
              <input className="input" value={ticketTitle} readOnly />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('timer.start')}</label>
              <input type="datetime-local" className="input text-sm" value={editStartTime}
                onChange={(e) => setEditStartTime(e.target.value)} required />
            </div>
            <div>
              <label className="label">{t('timer.end')}</label>
              <input type="datetime-local" className="input text-sm" value={editEndTime}
                onChange={(e) => setEditEndTime(e.target.value)} required />
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 py-2 bg-info-50 rounded-lg">
            <Clock size={16} className="text-info-600" />
            <span className="text-sm text-blue-800">
              {t('timer.duration')}<strong>{Math.floor(computedDuration / 60)}:{String(computedDuration % 60).padStart(2, '0')}</strong> {t('common.hours')}
            </span>
          </div>

          <div>
            <label className="label">{t('timer.summaryLabel')}</label>
            <textarea
              className="input min-h-[100px] text-sm"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              required
              minLength={20}
              rows={4}
              placeholder={t('timer.summaryPlaceholder')}
            />
            {summary.length > 0 && summary.length < 20 && (
              <p className="text-xs text-warning-600 mt-1">{t('timer.charsRemaining', { count: 20 - summary.length })}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="timer-billable" checked={billable}
              onChange={(e) => setBillable(e.target.checked)} className="w-4 h-4 accent-primary-600" />
            <label htmlFor="timer-billable" className="text-sm text-surface-700">{t('common.billable')}</label>
          </div>

          {mutation.error && (
            <div className="bg-danger-50 border border-danger-100 text-danger-700 px-4 py-3 rounded-lg text-sm">
              {(mutation.error as any)?.response?.data?.error?.message || t('timer.saveError')}
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={mutation.isPending || !isValid}
              className="btn btn-primary flex-1 disabled:opacity-50">
              {mutation.isPending ? t('common.saving') : t('timer.saveRecord')}
            </button>
            <button type="button" onClick={onClose}
              className="btn btn-secondary flex-1">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
